import { Checkbox, InlineNotification, Modal, TextArea } from '@carbon/react';
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { flagCategoryLabel } from 'components/entity-form/flag-categories';
import style from './flag-override-modal.module.scss';

export interface FlaggedEntitySummary {
  id: number;
  // The row the user saw. On the reconcile screen a row is a collapsed alias
  // family, so `name` is the display (most-similar) member's name; when the flag
  // belongs to a different member, `aliasName` attributes it and `rowEntityId`
  // anchors an optional "view alias graph" link.
  name: string | null;
  aliasName?: string | null;
  rowEntityId?: number;
  // Whether the row belongs to an alias family — gates the "view alias graph"
  // link, since a lone entity has nothing to graph.
  rowAliased?: boolean;
  flagCategory: string | null;
  flagRationale: string | null;
}

// Shape the backend returns (HTTP 422) when a combine/alias/delete touches a
// flagged entity that is part of a title workspace and isn't yet resolved. See
// ConsolidationFlagGate and Reconcile::Orchestrator.
export interface FlagOverrideRequiredResponse {
  error?: string;
  flagOverrideRequired: true;
  flaggedEntities: FlaggedEntitySummary[];
}

export const isFlagOverrideRequired = (
  data: unknown
): data is FlagOverrideRequiredResponse =>
  typeof data === 'object' &&
  data !== null &&
  (data as { flagOverrideRequired?: unknown }).flagOverrideRequired === true;

// Stable class on this modal's root. When opened from inside another Carbon Modal
// (the reconcile tearsheets / Add-Alias), pass FLAG_OVERRIDE_MODAL_SELECTOR to
// that parent Modal's `selectorsFloatingMenus` so its focus trap treats this
// modal as "outside" and stops yanking focus back to its own close button.
export const FLAG_OVERRIDE_MODAL_CLASS = 'flag-override-modal-root';
export const FLAG_OVERRIDE_MODAL_SELECTOR = `.${FLAG_OVERRIDE_MODAL_CLASS}`;

interface Props {
  open: boolean;
  flaggedEntities: FlaggedEntitySummary[];
  loading?: boolean;
  // A failed submit, kept beside the rationale for a retry.
  error?: string | null;
  onCancel: () => void;
  // rationale is the reconciliation-level reason; falsePositiveIds are the flags
  // the user marked as false positives.
  onConfirm: (rationale: string, falsePositiveIds: number[]) => void;
  // Optional per-row affordance to open the alias-family graph (reconcile flows,
  // where a row is a collapsed family). Omitted where rows are single entities.
  renderRowGraphLink?: (rowEntityId: number) => ReactNode;
  // Selectors for any modal this one opens (e.g. the alias-cluster graph) so this
  // modal's focus trap treats them as "outside" and doesn't yank focus back.
  floatingMenuSelectors?: string[];
}

// The consolidation-risk warning/override gate UI (PROD/2907373590). Lists each
// gated flag with a "False positive" checkbox. A single reconciliation-level
// rationale is REQUIRED unless every flag is marked a false positive, in which
// case it's optional. Marking a false positive is recorded for this action only;
// it never clears the flag.
export const FlagOverrideModal = ({
  open,
  flaggedEntities,
  loading = false,
  error,
  onCancel,
  onConfirm,
  renderRowGraphLink,
  floatingMenuSelectors,
}: Props) => {
  const [rationale, setRationale] = useState('');
  const [falsePositiveIds, setFalsePositiveIds] = useState<Set<number>>(
    new Set()
  );

  // Reset each time the modal opens so a prior attempt never carries over.
  useEffect(() => {
    if (open) {
      setRationale('');
      setFalsePositiveIds(new Set());
    }
  }, [open]);

  const toggleFalsePositive = (id: number, checked: boolean) =>
    setFalsePositiveIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  const allFalsePositive =
    flaggedEntities.length > 0 &&
    flaggedEntities.every((flagged) => falsePositiveIds.has(flagged.id));
  const rationaleRequired = !allFalsePositive;
  const canProceed =
    !loading && (!rationaleRequired || rationale.trim().length > 0);

  return createPortal(
    <Modal
      danger
      size="sm"
      className={FLAG_OVERRIDE_MODAL_CLASS}
      selectorsFloatingMenus={floatingMenuSelectors}
      open={open}
      modalHeading="Consolidation-risk warning"
      primaryButtonText={loading ? 'Overriding…' : 'Override and proceed'}
      primaryButtonDisabled={!canProceed}
      secondaryButtonText="Cancel"
      onRequestSubmit={() => {
        if (canProceed)
          onConfirm(rationale.trim(), Array.from(falsePositiveIds));
      }}
      onRequestClose={onCancel}
    >
      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Error"
          subtitle={error}
          className={style.error}
        />
      )}
      <InlineNotification
        kind="warning"
        lowContrast
        hideCloseButton
        title="This action involves a flagged entity"
        subtitle="Flagged entities may represent legally or functionally distinct parties. Mark any false positives; a reason is required only when overriding a real flag."
      />

      <ul className={style.flaggedList}>
        {flaggedEntities.map((flagged) => (
          <li key={flagged.id} className={style.flaggedRow}>
            <div>
              <strong>{flagged.name ?? `Entity ${flagged.id}`}</strong> —{' '}
              {flagCategoryLabel(flagged.flagCategory) || 'Flagged'}
              {flagged.flagRationale ? `: ${flagged.flagRationale}` : ''}
            </div>
            {/* When the flag belongs to an alias member other than the row's
                display name, say so — the display name stays what the user saw
                on the reconcile page. */}
            {flagged.aliasName ? (
              <div className={style.aliasAttribution}>
                Based on alias name: {flagged.aliasName}
              </div>
            ) : null}
            <div className={style.actions}>
              <Checkbox
                className={style.falsePositive}
                id={`flag-false-positive-${flagged.id}`}
                labelText="False positive"
                checked={falsePositiveIds.has(flagged.id)}
                onChange={(_e: unknown, { checked }: { checked: boolean }) =>
                  toggleFalsePositive(flagged.id, checked)
                }
              />
              {renderRowGraphLink &&
              flagged.rowEntityId !== undefined &&
              flagged.rowAliased
                ? renderRowGraphLink(flagged.rowEntityId)
                : null}
            </div>
          </li>
        ))}
      </ul>

      <TextArea
        id="flag-override-rationale"
        labelText={`Reason for overriding ${
          rationaleRequired ? '(required)' : '(optional)'
        }`}
        rows={3}
        value={rationale}
        onChange={({ target: { value } }) => setRationale(value)}
      />
    </Modal>,
    document.body
  );
};
